// Notificacoes DERIVADAS do estado real (propostas + folha). Nao mantem
// lista propria — calcula on-the-fly. So persiste o estado lida/nao-lida
// por ID em localStorage.

import { fmtDateTime, getAllPropostasForMatricula, type Proposta } from "./propostas-data";
import { readActiveIdMatricula } from "./matricula-data";
import { accountKey } from "./query-account";

export type NotifType =
  | "proposta_enviada"
  | "proposta_em_analise"
  | "proposta_aprovada"
  | "proposta_aguardando_adf"
  | "proposta_averbada"
  | "proposta_aguardando_formalizacao"
  | "proposta_recusada"
  | "proposta_cancelada"
  | "proposta_expirando"
  | "proposta_editada_pelo_banco"
  | "folha_processada"
  | "oferta_banco"
  | "portabilidade_a_confirmar"
  | "portabilidade_concluida";

export interface Notification {
  id: string;
  type: NotifType;
  titulo: string;
  mensagem: string;
  quando: string;
  /** Link interno (ex.: /servidor/contratos#PRO-9803). */
  href: string;
  /** Link externo opcional (ex.: link do banco pra formalizar). Quando presente, abre em nova aba. */
  externalLink?: string;
  /** Unico estado de leitura. Governa AO MESMO TEMPO a bolinha dourada do item
   *  e o contador do sino — separar os dois (tentativa de 07/08/2026 manha)
   *  criava contradicao visivel: badge zerado com 29 bolinhas de "nao lida"
   *  do lado. Se tem bolinha, tem contagem. */
  lida: boolean;
  // Timestamp interno pra ordenacao (mais recentes primeiro). Nao e renderizado.
  _ts?: number;
}

/**
 * Estado de lido/dispensado guardado POR CONTA.
 *
 * Antes era uma chave global (`atlas:notifications:read`), e por isso o logout
 * a apagava: sem separação por conta, a leitura de um servidor apareceria pro
 * próximo que logasse no mesmo navegador. O efeito colateral era o que o
 * cliente reportou em 13/08/2026 — marcar como lido, sair e voltar, e tudo
 * estar não-lido de novo. Não era bug de gravação: era limpeza deliberada.
 *
 * Com a chave escopada pela conta (mesmo `accountKey()` que já isola o cache
 * do React Query), uma conta não enxerga a outra, e aí não há mais motivo pra
 * apagar no logout — o estado sobrevive. Ver session.ts.
 *
 * Deslogado, `accountKey()` é vazio e cai na chave base: é o que o app já
 * fazia, e nesse momento não há sino na tela.
 */
const READ_KEY_BASE = "atlas:notifications:read";
const DISMISSED_KEY_BASE = "atlas:notifications:dismissed";

function chaveDaConta(base: string): string {
  const acct = accountKey();
  return acct ? `${base}:${acct}` : base;
}

function idsDaChave(k: string): string[] {
  try {
    const raw = window.localStorage.getItem(k);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

/**
 * Lê o estado da conta atual, adotando UMA VEZ o que houver na chave global
 * antiga.
 *
 * A adoção move e apaga, não copia. Se a chave global continuasse sendo lida
 * para sempre, ela viraria um vazamento entre contas — exatamente o risco que
 * justificava limpar tudo no logout. Movendo, o dado vai para quem estava
 * logado quando a migração rodou (que é de quem ele é) e some da área comum.
 *
 * Sem conta definida (deslogado) não migra nada: não há para onde mover.
 */
function lerIds(base: string): Set<string> {
  const acct = accountKey();
  const chave = chaveDaConta(base);
  const set = new Set<string>(idsDaChave(chave));
  if (!acct) return set;

  const legado = idsDaChave(base);
  if (legado.length > 0) {
    for (const id of legado) set.add(id);
    try {
      window.localStorage.setItem(chave, JSON.stringify([...set]));
      window.localStorage.removeItem(base);
    } catch { /* ignore */ }
  }
  return set;
}
/** LEGADO — chave da tentativa de separar "vista" de "lida". Nao e mais
 *  escrita; so lida na migracao abaixo, pra quem ja tinha visto sob aquela
 *  versao nao reaparecer como nao-lido. */
const SEEN_KEY = "atlas:notifications:seen";

function readReadIds(): Set<string> {
  const set = lerIds(READ_KEY_BASE);
  // Migracao do estado "vista" (versao anterior de 07/08/2026): quem ja tinha
  // ABERTO o sino com aquelas notificacoes listadas nao pode ve-las voltar
  // como nao-lidas agora que os dois estados viraram um so. Cliente foi
  // explicito: "se eu ja marquei como lido, nao e pra voltar a ficar como
  // nao lido". Idempotente — so soma ids, nunca remove.
  //
  // Tambem MOVE em vez de so ler: esta chave e global e nunca foi limpa no
  // logout, entao continuava valendo pra qualquer conta que abrisse o app
  // neste navegador. Mesmo tratamento da chave de lidas.
  const vistos = idsDaChave(SEEN_KEY);
  if (vistos.length > 0) {
    for (const id of vistos) set.add(id);
    if (accountKey()) {
      try {
        window.localStorage.setItem(chaveDaConta(READ_KEY_BASE), JSON.stringify([...set]));
        window.localStorage.removeItem(SEEN_KEY);
      } catch { /* ignore */ }
    }
  }
  return set;
}

function writeReadIds(set: Set<string>): void {
  try {
    window.localStorage.setItem(chaveDaConta(READ_KEY_BASE), JSON.stringify([...set]));
  } catch {
    // ignore
  }
}

function readDismissedIds(): Set<string> {
  return lerIds(DISMISSED_KEY_BASE);
}

function writeDismissedIds(set: Set<string>): void {
  try {
    window.localStorage.setItem(chaveDaConta(DISMISSED_KEY_BASE), JSON.stringify([...set]));
  } catch { /* ignore */ }
}


/** Dispensa todas as notificacoes atuais — some do dropdown do sino. Chamado
 *  pelo botao "Limpar tudo". Notificacoes NOVAS (estados futuros da proposta)
 *  continuam aparecendo — so o snapshot atual e escondido. */
export function dismissAll(notifIds: string[]): void {
  const set = readDismissedIds();
  for (const id of notifIds) set.add(id);
  writeDismissedIds(set);
}

/** Filtra ids dispensados — usado pelo builder. */
export function isDismissed(id: string): boolean {
  return readDismissedIds().has(id);
}

export function markAsRead(notifId: string): void {
  const set = readReadIds();
  set.add(notifId);
  writeReadIds(set);
}

export function markAllAsRead(notifIds: string[]): void {
  const set = readReadIds();
  for (const id of notifIds) set.add(id);
  writeReadIds(set);
}

// Parseia data em ISO ou DD/MM/YYYY. Retorna null quando nao reconhece.
function tsDe(criadaEm: string | undefined | null): number | null {
  if (!criadaEm) return null;
  const iso = Date.parse(criadaEm);
  if (Number.isFinite(iso)) return iso;
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(criadaEm);
  if (m) {
    const t = new Date(`${m[3]}-${m[2]}-${m[1]}T00:00:00`).getTime();
    return Number.isFinite(t) ? t : null;
  }
  return null;
}

function tempoRelativo(criadaEm: string): string {
  // Aceita ISO ou texto formatado. Para textos hardcoded ja formatados, devolve o proprio texto.
  const tentativa = new Date(criadaEm);
  if (Number.isNaN(tentativa.getTime())) return criadaEm;
  const diffMs = Date.now() - tentativa.getTime();
  const min = Math.floor(diffMs / 60000);
  if (min < 1) return "agora";
  if (min < 60) return `ha ${min}min`;
  const h = Math.floor(min / 60);
  if (h < 24) return `ha ${h}h`;
  const d = Math.floor(h / 24);
  if (d < 7) return `ha ${d}d`;
  return tentativa.toLocaleDateString("pt-BR");
}

/** Data em que a proposta ENTROU numa dada etapa. `chaves` sao as situacoes do
 *  backend (lowercase) que representam essa etapa — a primeira encontrada
 *  vence. Sem carimbo (proposta anterior ao recurso, ou etapa que coincide com
 *  a criacao), devolve null e o chamador cai na data de criacao.
 *  Casa por prefixo pra tolerar variacoes ("aguardando confirmação do
 *  deferimento" casa com "aguardando"). */
function tsDaSituacao(p: Proposta, chaves: string[]): number | null {
  const mapa = p.situacaoEm;
  if (!mapa) return null;
  const entradas = Object.entries(mapa);
  for (const chave of chaves) {
    const hit = entradas.find(([k]) => k === chave) ?? entradas.find(([k]) => k.startsWith(chave));
    if (hit) {
      const t = tsDe(hit[1]);
      if (t != null) return t;
    }
  }
  return null;
}

/** Emite UMA notificacao POR ETAPA que a proposta ja alcancou — assim o
 *  servidor ve a timeline inteira no sino ("Proposta enviada" → "Em analise"
 *  → "Aprovada" → "Aguardando ADF" → "Averbada"). O estado atual determina
 *  quantas etapas foram alcancadas — nao emite futuras. Cada etapa e datada
 *  pelo carimbo da transicao correspondente (situacaoEm), nao pela criacao. */
function notifsFromProposta(p: Proposta): Notification[] {
  const internalHref = `/servidor/contratos#${p.id}`;
  const quando = tempoRelativo(p.criadaEm);
  const ts = tsDe(p.criadaEm) ?? 0;
  /** Data/rotulo de uma ETAPA especifica. Sem carimbo real, herda a criacao —
   *  comportamento antigo — mas com `desempate` pra ordenacao continuar
   *  cronologica dentro da mesma proposta. */
  const etapa = (chaves: string[], desempate: number) => {
    const t = tsDaSituacao(p, chaves);
    return t == null
      ? { quando, _ts: ts + desempate }
      : { quando: tempoRelativo(new Date(t).toISOString()), _ts: t };
  };
  const list: Notification[] = [];

  // ETAPA 1 — Proposta enviada. Sempre emitida (mesmo que ja tenha avancado).
  list.push({
    id: `proposta:${p.id}:enviada`,
    type: "proposta_enviada",
    titulo: `Proposta ${p.id} enviada`,
    mensagem: `Sua pré-reserva foi registrada no Atlas e enviada ao ${p.banco}.`,
    quando,
    href: internalHref,
    lida: false,
    _ts: ts,
  });

  // Fluxos negativos — encerram a timeline aqui.
  if (p.estado === "recusada") {
    list.push({
      id: `proposta:${p.id}:recusada`,
      type: "proposta_recusada",
      titulo: `Proposta ${p.id} recusada`,
      mensagem: p.motivoRecusa ?? `O ${p.banco} recusou sua pré-reserva.`,
      href: internalHref,
      lida: false,
      ...etapa(["recusado", "reprovado", "rejeitado", "negado", "cancelado"], 10),
    });
    return list;
  }
  if (p.estado === "cancelada" || p.estado === "expirada") {
    list.push({
      id: `proposta:${p.id}:cancelada`,
      type: "proposta_cancelada",
      titulo: `Proposta ${p.id} cancelada`,
      mensagem: p.estado === "expirada"
        ? `Sua pré-reserva expirou (48h sem resposta da instituição). Margem liberada.`
        : `Sua pré-reserva foi cancelada e a margem voltou a ficar disponível.`,
      href: internalHref,
      lida: false,
      // Expirada: prioriza o carimbo de expiracao (a hora real do vencimento,
      // nao a da leitura que detectou). Cancelada: o carimbo do cancelamento.
      ...etapa(p.estado === "expirada" ? ["expirado", "cancelado"] : ["cancelado", "expirado"], 10),
    });
    return list;
  }

  // ETAPA 2 — Em analise pelo banco. Alcancada assim que a proposta existe.
  list.push({
    id: `proposta:${p.id}:em_analise`,
    type: "proposta_em_analise",
    titulo: `Proposta ${p.id} em análise`,
    mensagem: `O ${p.banco} está avaliando sua pré-reserva. Você será avisado quando responder.`,
    quando,
    href: internalHref,
    lida: false,
    _ts: ts + 1,
  });

  // BANCO EDITOU VALORES (fluxo negociacao offline). Emite UMA notif por
  // proposta com "antes / depois" pra o servidor ver o que foi acordado.
  // Aparece independente do estado (permanece visivel ate ser dispensada).
  if (p.editadaPeloBanco) {
    const ed = p.editadaPeloBanco;
    const editadoTs = tsDe(ed.editadoEm) ?? ts;
    list.push({
      id: `proposta:${p.id}:editada`,
      type: "proposta_editada_pelo_banco",
      titulo: `${p.banco} atualizou sua proposta ${p.id}`,
      mensagem:
        `Antes: R$ ${ed.valorOriginal.toFixed(2)} em ${ed.parcelasOriginal}x @ ${(ed.taxaAmOriginal * 100).toFixed(2)}% a.m. — ` +
        `Agora: R$ ${p.valor.toFixed(2)} em ${p.parcelas}x @ ${(p.taxaAm * 100).toFixed(2)}% a.m.` +
        (ed.motivo ? ` — Motivo: ${ed.motivo}` : ""),
      quando: tempoRelativo(ed.editadoEm),
      href: internalHref,
      lida: false,
      _ts: editadoTs,
    });
  }

  // ETAPA 3 — Aprovada pelo banco.
  if (p.estado === "aprovada" || p.estado === "aguardando_formalizacao" || p.estado === "liberada") {
    list.push({
      id: `proposta:${p.id}:aprovada`,
      type: "proposta_aprovada",
      titulo: `Proposta ${p.id} aprovada`,
      mensagem: `O ${p.banco} aprovou sua proposta. A averbadora vai processar a ADF nas próximas horas.`,
      href: internalHref,
      lida: false,
      ...etapa(["aprovado"], 2),
    });
  }

  // ETAPA 4 — Aguardando ADF da averbadora (apos aprovacao, antes de virar Ativo).
  if (p.estado === "aprovada") {
    list.push({
      id: `proposta:${p.id}:aguardando_adf`,
      type: "proposta_aguardando_adf",
      titulo: `Aguardando averbadora aplicar ${p.id}`,
      mensagem: `A averbadora Atlas vai aplicar sua ADF na folha da prefeitura em breve.`,
      href: internalHref,
      lida: false,
      // Mesma origem da aprovacao (a espera pela ADF comeca ali), +1ms pra
      // ficar logo abaixo dela na ordenacao.
      ...(() => { const e = etapa(["aprovado"], 3); return { ...e, _ts: e._ts + 1 }; })(),
    });
  }

  // ETAPA 4b — Aguardando assinatura (fluxo pos-formalizacao, se aplicavel).
  if (p.estado === "aguardando_formalizacao") {
    list.push({
      id: `proposta:${p.id}:aguardando_formalizacao`,
      type: "proposta_aguardando_formalizacao",
      titulo: `Aguardando assinatura de ${p.id}`,
      mensagem: p.expiraEm
        ? `Assine com o ${p.banco} (por telefone, e-mail ou app da instituição). Trava expira em ${fmtDateTime(p.expiraEm)}.`
        : `Assine com o ${p.banco} — a instituição entrará em contato pelo canal dela.`,
      href: internalHref,
      lida: false,
      ...etapa(["aguardando formaliza", "aprovado"], 3),
    });
  }

  // ETAPA 5 — Averbacao completa (ADF aplicada em folha).
  if (p.estado === "liberada") {
    list.push({
      id: `proposta:${p.id}:averbada`,
      type: "proposta_averbada",
      titulo: `Averbação de ${p.id} confirmada`,
      mensagem: `Sua ADF foi aplicada na folha da prefeitura. O contrato está ativo e o desconto começa na próxima competência.`,
      href: internalHref,
      lida: false,
      ...etapa(["ativo", "averbado"], 4),
    });
  }

  return list;
}

/**
 * Constroi a lista de notificacoes para a matricula ativa, com estado
 * lida/nao-lida aplicado. Le propostas do localStorage (legado).
 */
export function buildNotifications(): Notification[] {
  const idMatricula = readActiveIdMatricula();
  const propostas = getAllPropostasForMatricula(idMatricula);
  return buildNotificationsFromPropostas(propostas);
}

/**
 * Versao que aceita propostas ja carregadas (idealmente do backend via
 * useQuery). Isso mantem sino e /servidor/contratos na MESMA fonte de
 * verdade, evitando notificar sobre proposta que ja nao existe no DB.
 * Opcionalmente inclui ofertas de credito criadas pelos bancos.
 */
export interface OfertaBancoParaNotif {
  id: string;
  bancoNome: string;
  titulo: string;
  mensagem: string;
  taxaAm: number;
  parcelasMax: number;
  valorMax: number;
  criadoEm: string;
  expiraEm?: string | null;
}
/** "1h 23min" ou "45min" ou "12s". Para countdown de promocao relampago. */
function formatCountdown(msRestantes: number): string {
  const total = Math.max(0, Math.floor(msRestantes / 1000));
  const d = Math.floor(total / 86400);
  const h = Math.floor((total % 86400) / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  if (d > 0) return `${d}d ${h}h`;
  if (h > 0) return `${h}h ${String(m).padStart(2, "0")}min`;
  if (m > 0) return `${m}min ${String(s).padStart(2, "0")}s`;
  return `${s}s`;
}
function notifDeOferta(o: OfertaBancoParaNotif): Notification | null {
  let sufixo = "";
  if (o.expiraEm) {
    const restante = new Date(o.expiraEm).getTime() - Date.now();
    if (restante <= 0) return null; // ja expirou — nao aparece no sino
    // Promocao relampago (≤ 24h): destaca o countdown.
    if (restante <= 24 * 3600 * 1000) sufixo = ` ⏰ Termina em ${formatCountdown(restante)}.`;
  }
  return {
    id: `oferta:${o.id}`,
    type: "oferta_banco",
    titulo: `${o.bancoNome}: ${o.titulo}`,
    // taxaAm vem em decimal cru (0.0179 = 1.79%). Antes o toFixed(2) sem *100
    // renderizava "0.02% a.m." — cliente reportou como bug de exibicao.
    mensagem: `${o.mensagem} — ${(o.taxaAm * 100).toFixed(2)}% a.m. em até ${o.parcelasMax}x.${sufixo}`,
    quando: tempoRelativo(o.criadoEm),
    // Rota valida — /servidor/ofertas nunca existiu (dava 404 e o router
    // caia no fallback com "Unexpected Application Error").
    href: "/servidor/marketplace/portabilidade",
    lida: false,
    _ts: tsDe(o.criadoEm) ?? 0,
  };
}
/** O que o sino precisa de uma portabilidade (`atlas.portabilidade.minhas`). */
export interface PortabilidadeParaNotif {
  id: string;
  estado: string;
  proponente: { nome: string };
  credora: { nome: string };
  origem: { adf: string };
  adfDestino: string | null;
  criadoEm: string;
  atualizadoEm: string;
  expiraEm: string | null;
}

/**
 * Duas etapas da portabilidade falam com o servidor: a que ESPERA por ele (o
 * banco de destino abriu e so' anda com a confirmacao dele) e o desfecho
 * (contrato antigo quitado, novo averbado). As do meio sao entre os bancos.
 * O id leva a etapa, para a confirmacao lida nao esconder a conclusao.
 */
function notifDePortabilidade(p: PortabilidadeParaNotif): Notification | null {
  if (p.estado === "aguardando_servidor") {
    return {
      id: `portabilidade:${p.id}:confirmar`,
      type: "portabilidade_a_confirmar",
      titulo: `Confirme a portabilidade para o ${p.proponente.nome}`,
      mensagem: `O ${p.proponente.nome} quer trazer o seu contrato ${p.origem.adf} (${p.credora.nome}). Nada acontece sem a sua confirmação.`,
      quando: tempoRelativo(p.criadoEm),
      href: "/servidor/portabilidade",
      lida: false,
      _ts: tsDe(p.criadoEm) ?? 0,
    };
  }
  if (p.estado === "concluida") {
    return {
      id: `portabilidade:${p.id}:concluida`,
      type: "portabilidade_concluida",
      titulo: "Portabilidade concluída",
      mensagem: `O contrato ${p.origem.adf} com o ${p.credora.nome} foi quitado; o novo${p.adfDestino ? ` (${p.adfDestino})` : ""} com o ${p.proponente.nome} já está averbado.`,
      quando: tempoRelativo(p.atualizadoEm),
      href: "/servidor/portabilidade",
      lida: false,
      _ts: tsDe(p.atualizadoEm) ?? 0,
    };
  }
  return null;
}

export function buildNotificationsFromPropostas(
  propostas: Proposta[],
  ofertasBanco: OfertaBancoParaNotif[] = [],
  portabilidades: PortabilidadeParaNotif[] = [],
): Notification[] {
  const readIds = readReadIds();
  const dismissedIds = readDismissedIds();
  // Cada proposta pode emitir varias notifs (uma por etapa da timeline).
  const derivadas = propostas.flatMap(notifsFromProposta);
  const ofertas = ofertasBanco
    .map(notifDeOferta)
    .filter((n): n is Notification => n != null);
  // folhaProcessada() removida — era notificacao mockada. Volta quando houver
  // webhook do RH persistido.
  const doRito = portabilidades.map(notifDePortabilidade).filter((x): x is Notification => x != null);
  const lista = [...derivadas, ...ofertas, ...doRito];
  // Ordena descendente pelo timestamp interno (mais recentes primeiro).
  // Tiebreaker: id descendente — se duas notifs tem o mesmo _ts (proposta
  // antiga sem ISO, so DD/MM/YYYY = midnight), a de id "maior" (proposta
  // criada depois — ADF sequencial crescente) fica no topo. Cliente pediu
  // 30/07/2026: ordem cronologica correta, nao insertion-order.
  lista.sort((a, b) => {
    const dt = (b._ts ?? 0) - (a._ts ?? 0);
    if (dt !== 0) return dt;
    return b.id.localeCompare(a.id);
  });
  // Filtra dispensadas (usuario clicou "Limpar tudo") e marca lidas.
  return lista
    .filter((n) => !dismissedIds.has(n.id))
    .map((n) => ({ ...n, lida: readIds.has(n.id) }));
}
